import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { OrganizationsService } from 'src/app/domain/organization/components/service/organizations.service';
import {
  MaSelectedItems,
  MaShowItems,
} from 'src/app/domain/recovery/component/recovery-market-analysis/pojo/ma-classes';
import {
  MaContributionChartArrays,
  MarketAnalysisByVintageChartDTO,
  VintageChartArraysMA,
} from '../../dto/MaByIndustryChartDTO';
import { MarketAnalysisService } from '../../service/market-analysis.service';
import { MarketAnalysisInternalService } from '../../service/marketAnalysisInternalService';
import { LocalDate } from '../../../../core/pojo/date-variable';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'app-market-analysis-by-vintage',
  templateUrl: './market-analysis-by-vintage.component.html',
  styleUrls: ['./market-analysis-by-vintage.component.scss'],
})
export class MarketAnalysisByVintageComponent implements OnInit, OnDestroy {
  private _destroyStream = new Subject<void>();

  marketAnalysisByVintageDto = new MarketAnalysisByVintageChartDTO();
  periods: LocalDate[] = [];
  selected = new MaSelectedItems();
  filteredVintagePeriods: LocalDate[] = [];

  initial = {
    periods: [] as LocalDate[],
    maData: new MarketAnalysisByVintageChartDTO(),
  };

  show = new MaShowItems();

  constructor(
    private organizationsService: OrganizationsService,
    private marketAnalysisService: MarketAnalysisService,
    private marketInternalService: MarketAnalysisInternalService,
  ) { }

  ngOnInit(): void {
    this.show.loadingState = true;
    this.initSelectedFundSubscription();
    this.initReportedPeriodSubscription();
    this.initVintagePeriodSubscriptions();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async getReportedPeriods(): Promise<void> {
    this.periods = await firstValueFrom(this.marketAnalysisService.getListOfReportedPeriods());
    if (!this.selected.period) {
      this.marketInternalService.sendReportedPeriods(this.periods);
    }
  }

  async getChartData(): Promise<void> {
    if (!this.selected.org) {
      return;
    }
    this.show.loadingState = true;
    this.marketAnalysisByVintageDto = await firstValueFrom(
      this.marketAnalysisService.getMarketAnalysisByVintageChartData(
        this.selected.period,
        this.selected.org.selectedFund.id,
      ).pipe(takeUntil(this._destroyStream)),
    );

    // Store the original data and periods
    this.initial.maData = cloneDeep(this.marketAnalysisByVintageDto);
    this.initial.periods = cloneDeep(this.marketAnalysisByVintageDto.tvpiChartData.periodsArr);

    this.handleVintagePeriods();
    this.show.loadingState = false;
  }

  private initSelectedFundSubscription(): void {
    this.organizationsService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        if (!org) {
          this.selected.org = undefined;
          return;
        }
        this.selected.org = org;
        if (this.selected.period) {
          this.getChartData();
        }
      });
  }

  private initReportedPeriodSubscription(): void {
    this.marketInternalService.selectedPeriodForMarketAnalysisState
      .pipe(takeUntil(this._destroyStream))
      .subscribe((selectedPeriod) => {
        if (!selectedPeriod) {
          this.getReportedPeriods();
        } else {
          this.selected.period = selectedPeriod;
          this.getChartData();
        }
      });
  }

  private initVintagePeriodSubscriptions(): void {
    this.marketInternalService.selectedVintageFromState
      .pipe(takeUntil(this._destroyStream))
      .subscribe((fromPeriod) => {
        if (fromPeriod) {
          this.selected.vintageFrom = fromPeriod;
          this.filterVintagePeriods(fromPeriod, this.selected.vintageTill);
        }
      });

    this.marketInternalService.selectedVintageTillState
      .pipe(takeUntil(this._destroyStream))
      .subscribe((tillPeriod) => {
        if (tillPeriod) {
          this.selected.vintageTill = tillPeriod;
          this.filterVintagePeriods(this.selected.vintageFrom, tillPeriod);
        }
      });
  }


  private handleVintagePeriods(): void {
    const vintagePeriods: LocalDate[] = [...this.marketAnalysisByVintageDto.tvpiChartData.periodsArr];

    if (vintagePeriods.length) {
      this.marketInternalService.sendVintagePeriods(vintagePeriods);
      this.marketInternalService.sendSelectedVintageFrom(vintagePeriods[0]);
      this.marketInternalService.sendSelectedVintageTill(vintagePeriods[vintagePeriods.length - 1]);
    }
  }

  private filterVintagePeriods(from: LocalDate, till: LocalDate): void {
    if (!from || !till) {
      return;
    }
    // Ensure the filtered periods only include valid dates
    this.filteredVintagePeriods = this.initial.periods.filter((date) => date >= from && date <= till);
    this.filterMarketAnalysisData();
  }

  private filterMarketAnalysisData(): void {
    const filteredPeriods = this.filteredVintagePeriods;

    this.marketAnalysisByVintageDto.tvpiChartData = this.filterChartData(this.initial.maData.tvpiChartData, filteredPeriods);
    this.marketAnalysisByVintageDto.moicChartData = this.filterChartData(this.initial.maData.moicChartData, filteredPeriods);
    this.marketAnalysisByVintageDto.netIrrChartData = this.filterChartData(this.initial.maData.netIrrChartData, filteredPeriods);
    this.marketAnalysisByVintageDto.grossIrrChartData = this.filterChartData(this.initial.maData.grossIrrChartData, filteredPeriods);
    this.marketAnalysisByVintageDto
      .contributionChartData = this.filterContributionData(this.initial.maData.contributionChartData, filteredPeriods);
  }

  private filterChartData(chartData: VintageChartArraysMA, filteredPeriods: LocalDate[]): VintageChartArraysMA {
    const indicesToKeep = this.getIndicesToKeep(chartData.periodsArr, filteredPeriods);

    return {
      topArr: this.filterByIndices(chartData.topArr, indicesToKeep),
      bottomArr: this.filterByIndices(chartData.bottomArr, indicesToKeep),
      medianArr: this.filterByIndices(chartData.medianArr, indicesToKeep),
      selectedFundArr: this.filterByIndices(chartData.selectedFundArr, indicesToKeep),
      periodsArr: this.filterByIndices(chartData.periodsArr, indicesToKeep),
    };
  }

  private filterContributionData(contributionData: MaContributionChartArrays, filteredPeriods: LocalDate[]): MaContributionChartArrays {
    const indicesToKeep = this.getIndicesToKeep(contributionData.periodsArr, filteredPeriods);

    return {
      averageContribution: this.filterByIndices(contributionData.averageContribution, indicesToKeep),
      restArr: this.filterByIndices(contributionData.restArr, indicesToKeep),
      periodsArr: this.filterByIndices(contributionData.periodsArr, indicesToKeep),
      selectedFundArr: this.filterByIndices(contributionData.selectedFundArr, indicesToKeep),
    };
  }

  private filterByIndices<T>(data: T[], indicesToKeep: number[]): T[] {
    return indicesToKeep.map((index) => data[index]);
  }

  private getIndicesToKeep(periodsArr: LocalDate[], filteredPeriods: LocalDate[]): number[] {
    return periodsArr
      .map((period, index) => (filteredPeriods.includes(period) ? index : -1))
      .filter((index) => index !== -1);
  }
}

